/**
 * XSS Protection Utility - server-safe sanitization helpers.
 */

const defaultConfig = {
    ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 'b', 'i', 's', 'strike',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li',
        'a', 'img',
        'div', 'span',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'blockquote', 'code', 'pre'
    ],
    ALLOWED_ATTR: [
        'href', 'src', 'alt', 'title', 'class', 'style',
        'width', 'height', 'target', 'rel'
    ],
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'style'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
};

const sanitizeHTMLServerFallback = (dirty) => {
    if (!dirty || typeof dirty !== 'string') return '';

    return dirty
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
        .replace(/<(iframe|object|embed)[^>]*>.*?<\/\1>/gis, '')
        .replace(/<(iframe|object|embed)[^>]*\/?>/gi, '')
        .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
        .replace(/\s(href|src)\s*=\s*(['"])\s*javascript:[^'"]*\2/gi, '');
};

/**
 * Sanitize HTML string using DOMPurify
 * SSR-safe implementation
 * 
 * @param {string} dirty - Unsafe HTML string
 * @param {object} options - DOMPurify config options
 * @returns {string} - Sanitized HTML string
 */
export const sanitizeHTML = (dirty, options = {}) => {
    if (!dirty || typeof dirty !== 'string') return '';

    if (typeof window === 'undefined') {
        return sanitizeHTMLServerFallback(dirty);
    }

    const config = { ...defaultConfig, ...options };

    if (typeof window.DOMPurify === 'undefined') {
        return sanitizeHTMLServerFallback(dirty);
    }

    return window.DOMPurify.sanitize(dirty, config);
};

/**
 * React Hook for sanitizing HTML with SSR support
 * Automatically handles client-side hydration
 * 
 * @param {string} dirty - Unsafe HTML string
 * @param {object} options - DOMPurify config options
 * @returns {string} - Sanitized HTML string (empty until mounted)
 */
export const sanitizeHTMLStrict = (dirty) => {
    return sanitizeHTML(dirty, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'b', 'i'],
        ALLOWED_ATTR: [],
        FORBID_TAGS: ['a', 'img', 'iframe', 'script', 'object', 'embed'],
    });
};

/**
 * Banner/Iframe sanitization - VERY strict
 * Only allow whitelisted domains
 */
export const sanitizeBanner = (dirty, allowedDomains = []) => {
    if (!dirty || typeof dirty !== 'string') return '';
    
    // Strip ALL script tags
    let clean = dirty.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    
    // If iframe, validate src domain
    if (clean.includes('<iframe')) {
        const srcMatch = clean.match(/src=["']([^"']+)["']/);
        if (srcMatch) {
            const url = srcMatch[1];
            const isAllowed = allowedDomains.some(domain => url.includes(domain));
            
            if (!isAllowed) {
                console.warn('⚠️ Blocked iframe from unauthorized domain:', url);
                return '<!-- Iframe blocked: Unauthorized domain -->';
            }
        }
    }
    
    return sanitizeHTML(clean, {
        ALLOWED_TAGS: ['iframe', 'div', 'span'],
        ALLOWED_ATTR: ['src', 'width', 'height', 'frameborder', 'allowfullscreen', 'class', 'style'],
    });
};
